import { css } from 'aphrodite';
import React from 'react';
import { formatBirthdayString } from '../../../../../../../models/UiUtils';
import { Button } from '../../../../../../../web/components/Button';
import { CompleteCheckIcon } from '../../../../../../../web/components/svgs/icons/CompleteCheckIcon';
import { FormFieldType, ICustomSurvey, ICustomSurveyResponse } from '../../../../../../models/Api';
import { CustomFieldFile } from './CustomFieldFile';
import { styleSheet } from './styles';

interface IProps {
	survey: ICustomSurvey;
	surveyResponse: ICustomSurveyResponse;
	onChangeResponseClick?: () => void;
}

export const CustomSurveyConfirmation = ({ survey, surveyResponse, onChangeResponseClick }: IProps) => {
	const confirmationMessage = React.useMemo(() => {
		const text = survey?.options?.confirmationMessage?.document;
		if (!text) {
			return null;
		}
		// remove lev-content tag
		const textWithoutLevContent = text.replace(/<lev-content[^>]*>/g, '').replace(/<\/lev-content>/g, '');
		// remove all p tag styles
		const textWithoutPStyles = textWithoutLevContent.replace(/<p[^>]*>/g, '<p>').replace(/<\/p>/g, '</p>');
		return <div dangerouslySetInnerHTML={{ __html: textWithoutPStyles }} className={css(styleSheet.thankYouContent)} />;
	}, [survey]);

	// Check if any address field has actual data to prevent displaying empty "Address:" label
	// This matches the pattern used for other optional contact fields (firstName, email, phone)
	const hasAddress = Boolean(
		surveyResponse?.contactInfo?.address?.address1 ||
			surveyResponse?.contactInfo?.address?.address2 ||
			surveyResponse?.contactInfo?.address?.city ||
			surveyResponse?.contactInfo?.address?.stateProvince ||
			surveyResponse?.contactInfo?.address?.postalCode ||
			surveyResponse?.contactInfo?.address?.country
	);

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.confirmationContent)}>
				<div className={css(styleSheet.icon)}>
					<CompleteCheckIcon />
				</div>
				<h2 className={css(styleSheet.header)}>Your response has been recorded!</h2>

				{confirmationMessage ?? <p className={css(styleSheet.message)}>Your response has been recorded.</p>}

				<div className={css(styleSheet.responseDetails)}>
					<h3 className={css(styleSheet.detailsHeader)}>Your Response</h3>
					{surveyResponse?.contactInfo?.firstName ? (
						<div className={css(styleSheet.detailItem)}>
							<span className={css(styleSheet.detailLabel)}>Name:</span>
							<span>{`${surveyResponse.contactInfo.firstName} ${surveyResponse.contactInfo.lastName}`}</span>
						</div>
					) : null}
					{surveyResponse?.contactInfo?.email ? (
						<div className={css(styleSheet.detailItem)}>
							<span className={css(styleSheet.detailLabel)}>Email:</span>
							<span>{surveyResponse.contactInfo.email}</span>
						</div>
					) : null}
					{surveyResponse?.contactInfo?.phoneNumber && (
						<div className={css(styleSheet.detailItem)}>
							<span className={css(styleSheet.detailLabel)}>Phone:</span>
							<span>{surveyResponse.contactInfo.phoneNumber}</span>
						</div>
					)}
					{surveyResponse?.contactInfo?.birthday && (
						<div className={css(styleSheet.detailItem)}>
							<span className={css(styleSheet.detailLabel)}>Birthday:</span>
							<span>{formatBirthdayString(surveyResponse.contactInfo.birthday)} </span>
						</div>
					)}
					{hasAddress && (
						<div className={css(styleSheet.detailItem)}>
							<span className={css(styleSheet.detailLabel)}>Address:</span>
							<div className={css(styleSheet.addressDetails)}>
								{surveyResponse.contactInfo.address.address1 && (
									<div>{surveyResponse.contactInfo.address.address1}</div>
								)}
								{surveyResponse.contactInfo.address.address2 && (
									<div>{surveyResponse.contactInfo.address.address2}</div>
								)}
								{(surveyResponse.contactInfo.address.city ||
									surveyResponse.contactInfo.address.stateProvince ||
									surveyResponse.contactInfo.address.postalCode) && (
									<div>
										{[
											surveyResponse.contactInfo.address.city,
											surveyResponse.contactInfo.address.stateProvince,
											surveyResponse.contactInfo.address.postalCode,
											surveyResponse.contactInfo.address.country,
										]
											.filter(Boolean)
											.join(', ')}
									</div>
								)}
							</div>
						</div>
					)}

					{surveyResponse.customProperties && Object.entries(surveyResponse.customProperties).length > 0 && (
						<div className={css(styleSheet.customProperties)}>
							<h4 className={css(styleSheet.customPropertiesHeader)}>Additional Information</h4>
							{Object.entries(surveyResponse.customProperties)?.map(([key, value]) => {
								const field = survey.customForm?.fields?.find(f => f.id === key);
								if (field.fieldType === FormFieldType.MultipleOptions || field.fieldType === FormFieldType.Option) {
									const multipleAnswers = ((value as string)?.split(',') ?? [])?.filter(Boolean);
									const answersFromOptions = field.options
										?.filter(o => multipleAnswers?.includes(o.id))
										.map(o => o.label);

									const answer = answersFromOptions?.join(', ') || '';
									return (
										<div key={key} className={css(styleSheet.detailItem)}>
											<span className={css(styleSheet.detailLabel)}>{field?.label || key}:</span>
											<p>{answer}</p>
										</div>
									);
								}

								if (field.fieldType === FormFieldType.FileUpload) {
									return (
										<CustomFieldFile
											key={key}
											field={field}
											value={value as unknown as { fileName: string; attachmentId: string }[]}
										/>
									);
								}
								return (
									<div key={key} className={css(styleSheet.detailItem)}>
										<span className={css(styleSheet.detailLabel)}>{field?.label || key}:</span>
										<p>{(value as string) || ''}</p>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{onChangeResponseClick && (
					<div className={css(styleSheet.actionContainer)}>
						<Button type='button' kind='secondary' label='Edit Response' onClick={onChangeResponseClick} />
					</div>
				)}
			</div>
		</div>
	);
};
